import React from "react";
import PropTypes from "prop-types";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SearchIcon from "@mui/icons-material/Search";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import {
  MOBILE_PAGE_PAD_X,
  MOBILE_TOUCH_MIN_PX,
  MOBILE_TYPE,
} from "../../theme/mobileLayout";
import {
  HEADER_NAV_ORDER,
  getAuthenticatedNavItemLabel,
  getVisibleAuthenticatedNavItems,
  isAuthenticatedNavItemActive,
} from "../layouts/authenticatedNavigation";
import AccountMenuCreditsSection from "../layouts/AccountMenuCreditsSection";

const NAV_ICONS = {
  search: SearchIcon,
  chat: ChatBubbleOutlineIcon,
  workspaces: WorkspacesOutlinedIcon,
  conferences: EventOutlinedIcon,
  admin: AdminPanelSettingsOutlinedIcon,
};

const MENU_DIVIDER_SX = {
  mx: MOBILE_PAGE_PAD_X,
  my: 1.5,
  borderColor: "rgba(88, 89, 91, 0.12)",
};

const MENU_ROW_SX = {
  minHeight: MOBILE_TOUCH_MIN_PX,
  borderRadius: 2,
  mx: 1,
  px: 1,
};

/**
 * Primary mobile navigation + account menu.
 * Profile identity lives inside the drawer; the header uses the credit-ring avatar.
 */
export default function MobileNavMenu({
  open,
  onClose,
  onNavigate,
  pathname = "",
  role,
  darkMode = false,
  onToggleDarkMode,
  accountInitial = "A",
  accountName,
  onOpenAccount,
  onOpenAnalyticsPreferences,
  onLogout,
  navigationDisabled = false,
  version,
  creditsBalance = null,
  creditsLoading = false,
  creditsError = null,
  onRefreshCredits,
}) {
  const visiblePages = getVisibleAuthenticatedNavItems(HEADER_NAV_ORDER, role);

  const go = (path) => {
    if (navigationDisabled) return;
    onClose?.();
    onNavigate?.(path);
  };

  const handleOpenUsage = () => {
    onRefreshCredits?.();
    onClose?.();
    onNavigate?.("/settings/usage");
  };

  const handleOpenAccount = () => {
    onClose?.();
    onOpenAccount?.();
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      PaperProps={{
        "data-testid": "mobile-nav-menu",
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom, 0px)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 4,
          borderRadius: 999,
          bgcolor: "rgba(88, 89, 91, 0.28)",
          mx: "auto",
          mt: 1.25,
          mb: 0.75,
          flexShrink: 0,
        }}
        aria-hidden
      />

      <Typography
        sx={{
          ...MOBILE_TYPE.pageTitle,
          px: MOBILE_PAGE_PAD_X,
          pb: 1.25,
          flexShrink: 0,
        }}
      >
        Menu
      </Typography>

      <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0, pb: 1 }}>
        <List disablePadding sx={{ px: 0.5, pt: 0 }}>
          {visiblePages.map((page) => {
            const Icon = NAV_ICONS[page.id] || SearchIcon;
            const label = getAuthenticatedNavItemLabel(page);
            const selected = isAuthenticatedNavItemActive(page, pathname);
            return (
              <ListItemButton
                key={page.id}
                disabled={navigationDisabled}
                selected={selected}
                onClick={() => go(page.path)}
                sx={{
                  ...MENU_ROW_SX,
                  "&.Mui-selected": {
                    bgcolor: "secondary.main",
                    color: "primary.main",
                    "& .MuiListItemIcon-root": { color: "primary.main" },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ sx: { ...MOBILE_TYPE.body, fontWeight: 700 } }}
                />
              </ListItemButton>
            );
          })}
        </List>

        <Divider sx={MENU_DIVIDER_SX} />
        <AccountMenuCreditsSection
          balance={creditsBalance}
          loading={creditsLoading}
          error={creditsError}
          onOpenUsage={handleOpenUsage}
          onTopUp={handleOpenUsage}
          variant="mobile"
        />

        <Divider sx={MENU_DIVIDER_SX} />

        <ListItemButton
          onClick={handleOpenAccount}
          sx={{
            ...MENU_ROW_SX,
            alignItems: "center",
            py: 1.25,
            mb: 0.5,
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: "primary.main",
              fontSize: 16,
              fontWeight: 700,
              mr: 1.5,
            }}
          >
            {accountInitial}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ ...MOBILE_TYPE.body, fontWeight: 700 }} noWrap>
              {accountName || "Account"}
            </Typography>
            <Typography sx={{ ...MOBILE_TYPE.caption, color: "text.secondary", mt: 0.25 }}>
              Profile & settings
            </Typography>
          </Box>
        </ListItemButton>

        <List disablePadding sx={{ px: 0.5 }}>
          <ListItemButton onClick={handleOpenAccount} sx={MENU_ROW_SX}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <AccountCircleOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Account"
              primaryTypographyProps={{ sx: { ...MOBILE_TYPE.body, fontWeight: 600 } }}
            />
          </ListItemButton>

          {onOpenAnalyticsPreferences ? (
            <ListItemButton onClick={onOpenAnalyticsPreferences} sx={MENU_ROW_SX}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <TuneOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Analytics preferences"
                primaryTypographyProps={{ sx: { ...MOBILE_TYPE.body, fontWeight: 600 } }}
              />
            </ListItemButton>
          ) : null}

          <ListItemButton
            onClick={() => onToggleDarkMode?.()}
            sx={MENU_ROW_SX}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {darkMode ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
            </ListItemIcon>
            <ListItemText
              primary="Dark mode"
              primaryTypographyProps={{ sx: { ...MOBILE_TYPE.body, fontWeight: 600 } }}
            />
            <Switch
              edge="end"
              checked={Boolean(darkMode)}
              onClick={(event) => event.stopPropagation()}
              onChange={() => onToggleDarkMode?.()}
              inputProps={{ "aria-label": "menu dark mode" }}
            />
          </ListItemButton>
        </List>

        <Divider sx={MENU_DIVIDER_SX} />

        <List disablePadding sx={{ px: 0.5, pb: 0.5 }}>
          <ListItemButton
            onClick={() => {
              onClose?.();
              onLogout?.();
            }}
            sx={{ ...MENU_ROW_SX, color: "error.main" }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
              <LogoutOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Log Out"
              primaryTypographyProps={{ sx: { ...MOBILE_TYPE.body, fontWeight: 600 } }}
            />
          </ListItemButton>
        </List>

        {version ? (
          <Typography
            sx={{
              ...MOBILE_TYPE.caption,
              color: "text.secondary",
              px: MOBILE_PAGE_PAD_X,
              py: 1.5,
              textAlign: "center",
            }}
          >
            {`Alphasearch v${version}`}
          </Typography>
        ) : null}
      </Box>
    </SwipeableDrawer>
  );
}

MobileNavMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
  pathname: PropTypes.string,
  role: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  darkMode: PropTypes.bool,
  onToggleDarkMode: PropTypes.func,
  accountInitial: PropTypes.string,
  accountName: PropTypes.string,
  onOpenAccount: PropTypes.func,
  onOpenAnalyticsPreferences: PropTypes.func,
  onLogout: PropTypes.func,
  navigationDisabled: PropTypes.bool,
  version: PropTypes.string,
  creditsBalance: PropTypes.object,
  creditsLoading: PropTypes.bool,
  creditsError: PropTypes.string,
  onRefreshCredits: PropTypes.func,
};
